import React, { useEffect, useState } from 'react';
import { SplitPanelUtil } from '../../commonUtils/SplitPanelUtil';
import { Input, Button, Checkbox, Icon } from '@scuf/common';
import './ForgeOnboardingView.scss';
import i18next from '../../../i18next';
import { useAuthService } from '../../../auth/hooks/useAuthService';
import toasts from '../../commonUtils/toasts';
import { ReactComponent as RetryIcon } from '../../../assets/Retry.svg';
import { ReactComponent as ErrorIcon } from '../../../assets/error.svg';


interface IForgeOnboardingViewProps {
    selectedNode: any,
    baseURL: string
    selectedSitesList: (sites: any, fetchSites: any) => void;
    customerID: (sites: any) => void;
    resetFields: boolean;
    updatedSites: any;
    onboardText: string;
    onboardItems: () => void;
    isForgeSiteOnboarding: boolean;
    changeBtnText: () => void;
    retryHandler: (siteDetails: any)=> void;
}

export const ForgeOnboardingView = (
    props: IForgeOnboardingViewProps
) => {
    const { authFetch } = useAuthService();
    const [isLoading, setIsLoading] = useState(false);
    const [sitesList, setSitesList] = useState<any[]>([]);
    const [selectedSites, setSelectedSites] = useState<any[]>([]);
    const [resetFields, setResetFields] = useState(props.resetFields);
    const [customerId, setCustomerId] = useState("");
    const [selectAllLabel, setSelectAllLabel] = useState("");    

    const onClickFetchSites = () => {
        if (customerId !== '') {
            setIsLoading(true);
            props.customerID(customerId);
            let orgId = props.selectedNode ? props.selectedNode.data.rootNodeId : '';
            const body = {
                customerId
            }
            authFetch(`${props.baseURL}/api/v1/forgesync/orgs/${orgId}/sites`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            })
                .then(response => {
                    if (response.ok) {
                        response.json().then((data: any) => {
                            setResetFields(false);
                            setSelectedSites([]);
                            props.changeBtnText();
                            setSitesList(data.sites.map((item: any) => {
                                return {
                                    ...item,
                                    checked: false,
                                    status: i18next.t('notOnboarded')
                                }
                            }));
                        })
                    } else {
                        throw new Error(i18next.t('SiteFetchingFailure'));
                    }
                })
                .catch(e => {
                    toasts.error(i18next.t('SiteFetchingFailure'));
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }

    const getButton = () => {
        return (
            <div className="button-container">
                <Button type="primary" size="small" content={i18next.t('fetchSites')} onClick={onClickFetchSites} loading={isLoading} />
            </div>
        )
    }    

    const getRetryActionButton = (siteDetails: any) => {      
      return (
        <div className="retry-action-btn">
          <Button
            className="retry-btn"
            type="link"
            content="Retry"
            iconPosition="left"
            size="small"
            onClick={() => {
              props.retryHandler(siteDetails);
            }}
          >
            <RetryIcon className="status-icon" />
            <span className="retry-label-btn">Retry</span>
          </Button>
        </div>
      );
    };

    const onSelectAllClicked = (checked: boolean) => {
      if(checked === true) {
        setSelectAllLabel(i18next.t('onboardAllSites'))
      } else {
        setSelectAllLabel("")
      }
        let selectedSiteList = sitesList.map((item: any) => {
            return {
                ...item,
                checked: checked
            }
        });
        setSitesList([...selectedSiteList]);
        if (checked) {
            setSelectedSites([...selectedSiteList]);
        } else {
            setSelectedSites([]);
        }
    }

    const onSitesClicked = (checked: boolean, data: any) => {
        setSelectAllLabel("")
        let selectedSiteList = selectedSites;
        let updatedItem = {
            ...data,
            checked
        }

        setSitesList(sitesList.map((item: any) => {
            if (item.siteId === data.siteId) {
                return {
                    ...item,
                    checked
                }
            } else {
                return item;
            }
        }));
        if (checked) {
            selectedSiteList.push(updatedItem);
            setSelectedSites([...selectedSiteList]);
        } else {
            setSelectedSites(selectedSites.filter((item: any) => item.siteId !== data.siteId));
        }
    }

    useEffect(() => {
        if (selectedSites) {
            for (const item of selectedSites) {
                delete item.checked;
            }
            props.selectedSitesList(selectedSites, sitesList);
        }
    }, [selectedSites]);

    useEffect(() => {
        if (props.updatedSites) {
            setSitesList(props.updatedSites);
        }
    }, [props.updatedSites]);

    return (
      <SplitPanelUtil.PanelContentMain>
        <SplitPanelUtil.PanelContentMainSection>
          <div className="customer-id-upload-view">
            <Input
              className="customer-id-input"
              label={i18next.t('customerID')}
              placeholder={i18next.t('enterCustomerID')}
              onChange={value => {
                setResetFields(false);
                setCustomerId(value);
              }}
              icon={getButton()}
              value={resetFields ? '' : customerId}
            />
            <div className="sites-display-view">
              {resetFields ? null : isLoading ? (
                <div className="loader-display">
                  {i18next.t('loadingSites')}
                </div>
              ) : sitesList && sitesList.length > 0 ? (
                <div className="all-sites-container">
                  <span className="sites-info">{`${sitesList.length} sites found`}</span>
                  <div className="select-all-container">
                    <div className="select-message-btn-container">
                      <span className="select-container-label">
                        {i18next.t('selectAllSitesText')}
                      </span>
                      <div className="onboard-btn-wrapper">
                        <Button
                          type="secondary"
                          size="small"
                          onClick={props.onboardItems}
                          disabled={selectedSites.length <= 0}
                          loading={props.isForgeSiteOnboarding}
                        >
                          {selectAllLabel.length ? selectAllLabel :  props.onboardText}
                        </Button>
                      </div>
                    </div>

                    <div className="select-all-view">
                      <Checkbox
                        label={i18next.t('selectAll')}
                        onChange={(checked: boolean) =>
                          onSelectAllClicked(checked)
                        }
                        indeterminate={
                          selectedSites.length > 0 &&
                          sitesList.length === selectedSites.length
                        }
                      />
                      <div className="select-status">{i18next.t('status')}</div>
                      <div className="select-action">{i18next.t('Action')}</div>
                    </div>
                    <div className="select-sites-view">
                      {sitesList.map((item: any, index) => {
                        return (
                          <div className="select-sites-row" key={index}>
                            <div className='site-label-info'>
                            <Checkbox
                              label={item.siteName}
                              checked={item.checked}
                              onChange={(checked: boolean) =>
                                onSitesClicked(checked, item)
                              }
                            />
                            {item.provisioningStatus === 'failed' ? (
                              <span className="sync-error-label">
                                <ErrorIcon className='error-icon' />
                                <span className='label'>{i18next.t('provisioningFailedMessages')}</span>
                              </span>
                            ) : null}
                            </div>
                            <div className="sites-status">
                              {item.status === i18next.t('onboardingFailed') ? (
                                <>
                                  <Icon
                                    root="building"
                                    name="close-circled"
                                    exactSize="25"
                                  />{' '}
                                  {item.status}
                                </>
                              ) : item.status ===
                                i18next.t('onboardedSuccessfully') ? (
                                <>
                                  <Icon
                                    root="common"
                                    name="badge-check"
                                    exactSize="25"
                                  />{' '}
                                  {item.status}
                                </>
                              ) : (
                                item.status
                              )}
                            </div>
                            <div className="sites-actions">
                              {item.status === i18next.t('onboardingFailed') || item.provisioningStatus === 'failed' ? (
                                getRetryActionButton(item)
                              ) : item.provisioningStatus === 'completed' ? (
                                <>
                                  <span className="action-success">-</span>
                                </>
                              ) : (
                                <span className="no-action">-</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </SplitPanelUtil.PanelContentMainSection>
      </SplitPanelUtil.PanelContentMain>
    );
};

export default ForgeOnboardingView;
